import { Injectable, Injector } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthService, ConfigService, NoRestApiHelper } from '@services';
import { map, Observable } from 'rxjs';
import {
  IIntInvoiceRegister,
  IIntInvoiceRegisterCreatePayload,
  IIntInvoiceRegisterInsertPayload,
  IIntInvoiceRegisterPrintPayload,
  IIntInvoiceRegisterRemovePayload,
  IIntInvoiceRegisterUpdatePayload,
  IIntInvoiceRemoveFromRegisterPayload,
  InternationalInvoice,
} from '@models';
import { IListData } from '@models';
import * as _ from 'lodash';
import * as moment from 'moment/moment';
import { catchArrayBufferErrors, checkErrors, FailedObject, PartialSuccessResponse } from '@shared';
import { PrintService } from '../print/print.service';
import {
  InternationalRegisterCreateResponse,
  IInternationalRegisterDeleteResponse,
  IRegisterErrorItem,
  IRegisterWarningItem,
  IRegisterCreateResponse,
  IRegisterDeleteResponse,
  IRegisterSuccessItem,
} from '../../models/register.model';

@Injectable({
  providedIn: 'root',
})
export class IntRegistersApiService {

  constructor(
    protected http: HttpClient,
    protected configService: ConfigService,
    protected _noRestApiHelper: NoRestApiHelper,
    protected _authorizationService: AuthService,
    private printService: PrintService,
    private injector: Injector,
  ) {
  }

  insert(payload: IIntInvoiceRegisterInsertPayload): Observable<PartialSuccessResponse> {
    const observable= this.http
      .post(`${this.configService.get('apiUrl')}json/`, {
        system: this.configService.get('system'),
        modelName: 'InternationalGeneral',
        calledMethod: 'insertDocuments',
        methodProperties: {
          ...payload,
        },
      });

    return observable.pipe(
      map(data => this._noRestApiHelper.checkErrors({ data })),
      map(response => this.parseInsertResponseForPartialSuccess(response.data.data[0])),
    );
  }

  update(payload: IIntInvoiceRegisterUpdatePayload): Observable<InternationalRegisterCreateResponse> {
    const observable: any = this.http
      .post(`${this.configService.get('apiUrl')}json/`, {
        system: this.configService.get('system'),
        modelName: 'InternationalGeneral',
        calledMethod: 'updateScanSheet',
        methodProperties: {
          ...payload,
        },
      });

    return observable.pipe(
      map(data => this._noRestApiHelper.checkErrors({ data })),
      map(data => this.bindListToModel(data)),
    );
  }

  getRegisters(pageIndex: number, pageSize: number): Observable<IListData<IIntInvoiceRegister>> {
    const observable: any = this.http
      .post(`${this.configService.get('apiUrl')}json/`, {
        system: this.configService.get('system'),
        modelName: 'InternationalGeneral',
        calledMethod: 'getScanSheetList',
        methodProperties: {
          Page: pageIndex,
          Limit: pageSize,
        },
      });

    return observable.pipe(
      map(data => this._noRestApiHelper.checkErrors({ data })),
      map(data => this.bindListToModel(data)),
    );
  }

  getInvoices(registerRef: string, page?: number, limit?: number, getFullList?: number): Observable<IListData<InternationalInvoice>> {
    const observable: any = this.http
      .post(`${this.configService.get('apiUrl')}json/`, {
        system: this.configService.get('system'),
        modelName: 'InternationalGeneral',
        calledMethod: 'getDocumentList',
        methodProperties: {
          State: 'allOrders',
          ScanSheetNumber: registerRef,
          ...(page ? { Page: page } : {}),
          ...(limit ? { Limit: limit } : {}),
          ...(getFullList ? { GetFullList: getFullList } : {}),
        },
      });

    return observable.pipe(
      map(data => this._noRestApiHelper.checkErrors({ data })),
      map(data => this.bindInvoicesListToModel(data)),
    );
  }

  delete(payload: IIntInvoiceRegisterRemovePayload): Observable<IInternationalRegisterDeleteResponse> {
    const observable: any = this.http
      .post(`${this.configService.get('apiUrl')}json/`, {
        system: this.configService.get('system'),
        modelName: 'InternationalGeneral',
        calledMethod: 'deleteScanSheet',
        methodProperties: {
          ...payload,
        },
      });

    return observable.pipe(
      map(data => this._noRestApiHelper.checkErrors({ data })),
      map(data => checkErrors(data, this.injector, 'Помилка видалення')),
    );
  }

  print(payload: IIntInvoiceRegisterPrintPayload): Observable<any> {
    const observable = this.http
      .post(`${this.configService.get('apiUrl')}json/`, {
        system: this.configService.get('system'),
        modelName: 'InternetDocument',
        calledMethod: 'printFull',
        methodProperties: {
          ...payload,
        },
      }, { responseType: 'arraybuffer' });

    return observable.pipe(
      map(response => catchArrayBufferErrors(response)),
      map(data => this._noRestApiHelper.checkErrors({ data })),
      map(response => response.data),
      map(data => {
        this.printService.openBlob(data, payload.Type);
        return null;
      }),
    );
  }

  removeFromRegister(payload: IIntInvoiceRemoveFromRegisterPayload): Observable<PartialSuccessResponse | null> {
    const observable = this.http
      .post(`${this.configService.get('apiUrl')}json/`, {
        system: this.configService.get('system'),
        modelName: 'InternationalGeneral',
        calledMethod: 'removeDocuments',
        methodProperties: {
          ...payload,
        },
      });

    return observable.pipe(
      map(data => this._noRestApiHelper.checkErrors({ data })),
      map(data => this.parseRemoveResponseForPartialSuccess(data)),
    );
  }

  private parseRemoveResponseForPartialSuccess(response: IRegisterDeleteResponse): PartialSuccessResponse | null {
    if (!response) {
      return null;
    }

    const documentRefs = response.data.DocumentRefs;

    if (!documentRefs) {
      return null;
    }

    const responseDataErrors: IRegisterErrorItem[] = documentRefs.Errors ?? [];
    const success: IRegisterSuccessItem[] = documentRefs.Success ?? [];

    if (responseDataErrors.length === 0) {
      return {
        failed: [],
        total: {
          succeededAmount: success.length,
          failedAmount: 0,
          totalAmount: success.length + responseDataErrors.length,
        },
      };
    }

    const failed: FailedObject[] = [...responseDataErrors]
      .map((el: IRegisterErrorItem) => ({
        number: el.Number,
        errorMessage: el.Error,
      }));

    return {
      failed,
      total: {
        succeededAmount: success.length,
        failedAmount: failed.length,
        totalAmount: success.length + failed.length,
      },
    };
  }

  private parseInsertResponseForPartialSuccess(response: IRegisterCreateResponse): PartialSuccessResponse {
    if (!response) {
      return null;
    }
    const data = response;

    const responseDataErrors: IRegisterErrorItem[] = data.Data?.Errors ?? [];
    const responseDataWarnings: IRegisterWarningItem[] = data.Data?.Warnings ?? [];

    if (responseDataErrors.length === 0 && responseDataWarnings.length === 0) {
      return {
        Barcode: data.Number,
        succeeded: data.Data.Success.map(el => el.Number),
        failed: [],
        total: {
          succeededAmount: data.Data.Success.length,
          failedAmount: 0,
          totalAmount: data.Data.Success.length,
        },
      };
    }

    const failed: FailedObject[] = [...responseDataErrors, ...responseDataWarnings]
      .map((el: IRegisterErrorItem | IRegisterWarningItem) => ({
        number: (el as IRegisterWarningItem).Number || (el as IRegisterErrorItem).Ref,
        errorMessage: (el as IRegisterWarningItem).Warning || (el as IRegisterErrorItem).Error,
      }));

    return {
      Barcode: data.Number,
      Ref: data.Ref,
      succeeded: data.Success.map(el => el.Number),
      failed,
      total: {
        succeededAmount: data.Success.length,
        failedAmount: failed.length,
        totalAmount: data.Success.length + failed.length,
      },
    };
  }


  private bindListToModel(data: any): IListData<IIntInvoiceRegister> {
    return {
      list: _.map(data.data.data, (el: any) => {
        const invoiceRegister: IIntInvoiceRegister = {
          docNumber: el.Number,
          ref: el.Ref,
          printed: el.Printed,
          dateTime: el.DateTime ? moment(el.DateTime).toDate() : null,
          description: el.Description,
          count: el.Count,
        };
        return invoiceRegister;
      }),
      total: data.data.info.totalCount,
    };
  }

  private bindInvoicesListToModel(response: any): IListData<InternationalInvoice> {
    return {
      list: response.data.data,
      total: response.data.info.totalCount,
    };
  }
}
